import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { deleteQuizTier, getQuizTiers, saveQuizTier } from '../Api';
import { QuizTierDto, SaveQuizTierRequest } from '../models/dtos/QuizAdminDtos';

const emptyTier = (): SaveQuizTierRequest => ({
    fee: 0,
    oddsBoost: 1,
    name: '',
    nameRu: '',
    imgUrl: '',
});

export default function QuizTiersPage() {
    const [loading, setLoading] = React.useState(true);
    const [items, setItems] = React.useState<QuizTierDto[]>([]);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);
    const [form, setForm] = React.useState<SaveQuizTierRequest>(emptyTier());

    const load = React.useCallback(async () => {
        setLoading(true);
        setItems(await getQuizTiers());
        setLoading(false);
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setForm(emptyTier());
        setEditorOpen(true);
    };

    const openEdit = (tier: QuizTierDto) => {
        setForm({
            id: tier.id,
            fee: tier.fee,
            oddsBoost: tier.oddsBoost,
            name: tier.name,
            nameRu: tier.nameRu,
            imgUrl: tier.imgUrl,
        });
        setEditorOpen(true);
    };

    const onSave = async () => {
        await saveQuizTier(form);
        setEditorOpen(false);
        load();
    };

    const confirmDelete = async () => {
        if (deleteId != null) {
            await deleteQuizTier(deleteId);
            setDeleteId(null);
            load();
        }
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'nameRu', headerName: 'Name RU', flex: 1 },
        { field: 'fee', headerName: 'Fee', width: 100 },
        { field: 'oddsBoost', headerName: 'Odds boost', width: 120 },
        { field: 'imgUrl', headerName: 'Image URL', flex: 1 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => openEdit(params.row)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => setDeleteId(params.row.id)}>Delete</Button>
                </Stack>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">Quiz tiers</Typography>
                <Button variant="contained" onClick={openCreate}>Add tier</Button>
            </Stack>
            {loading ? <CircularProgress /> : (
                <DataGrid autoHeight rows={items} columns={columns} getRowId={(row) => row.id} disableRowSelectionOnClick />
            )}

            <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{form.id ? 'Edit tier' : 'Create tier'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
                        <TextField label="Name RU" value={form.nameRu ?? ''} onChange={(e) => setForm({ ...form, nameRu: e.target.value })} fullWidth />
                        <TextField label="Fee" type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: Number(e.target.value) })} fullWidth />
                        <TextField label="Odds boost" type="number" value={form.oddsBoost} onChange={(e) => setForm({ ...form, oddsBoost: Number(e.target.value) })} fullWidth />
                        <TextField label="Image URL" value={form.imgUrl ?? ''} onChange={(e) => setForm({ ...form, imgUrl: e.target.value })} fullWidth />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={onSave}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteId != null} onClose={() => setDeleteId(null)}>
                <DialogTitle>Delete tier?</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                    <Button color="error" onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
